import * as React from "react";
import { Building2, Hotel, Briefcase, Heart, ShoppingBag, CheckCircle2, ArrowRight, Mail, Phone, Clock, Shield, Users, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useI18n } from "@/i18n";
import { LocalizedLink } from "@/components/LocalizedLink";
import OptimizedImage from "@/components/ui/optimized-image";
import lunaProductImg from "@/assets/luna-m9-main.jpg";
import ariaProductImg from "@/assets/aria-series-product-main.jpg";
import officeImg from "@/assets/office-building.jpg";

const Business = () => {
  const { t, language } = useI18n();
  const seo = getSeoCopy("business", language);
  const industries = [
    {
      icon: Hotel,
      title: "Hospitality",
      description: "Hotels, resorts, spas",
      link: "/business/hospitality",
      gradient: "from-primary/10 to-primary/5"
    },
    {
      icon: Briefcase,
      title: "Corporate",
      description: "Offices, co-working spaces",
      link: "/business/corporate",
      gradient: "from-accent/10 to-accent/5"
    },
    {
      icon: Heart,
      title: "Healthcare",
      description: "Clinics, senior living",
      link: "/business/healthcare",
      gradient: "from-primary/10 to-primary/5"
    },
    {
      icon: ShoppingBag,
      title: "Retail",
      description: "Restaurants, shopping centers",
      link: "/business/retail",
      gradient: "from-accent/10 to-accent/5"
    }
  ];

  const benefits = [
    { text: "Volume discounts up to 40%", icon: CheckCircle2 },
    { text: "Professional installation included", icon: CheckCircle2 },
    { text: "24/7 commercial support", icon: CheckCircle2 },
    { text: "Extended commercial warranties", icon: CheckCircle2 }
  ];

  const process = [
    { step: "1", title: "Choose your industry", description: "Select your business sector" },
    { step: "2", title: "Get custom quote", description: "Receive tailored pricing" },
    { step: "3", title: "Professional installation", description: "Expert setup included" }
  ];

  return (
    <>
      <SeoHead route="business" />
      
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <Navigation />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
          
          <div className="container mx-auto max-w-6xl relative">
            <div className="text-center space-y-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm text-primary font-medium">
                <Building2 className="h-4 w-4" />
                B2B Solutions
              </div>
              
              <h1 className="font-inter text-5xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight">
                {seo.h1}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto font-light">
                Transform your business facilities with premium sanitary technology
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                <Button size="lg" className="text-lg px-8 h-14 group" asChild>
                  <LocalizedLink to="/contact">
                    Get Volume Pricing
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </LocalizedLink>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 h-14 bg-background border-2 hover:bg-muted" asChild>
                  <LocalizedLink to="/contact">
                    Schedule Consultation
                  </LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Industry Cards */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Select Your Industry
              </h2>
              <p className="text-muted-foreground text-lg">
                Tailored solutions for your business sector
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {industries.map((industry) => (
                <LocalizedLink key={industry.title} to={industry.link}>
                  <Card className={`p-6 sm:p-8 hover:shadow-luxury transition-all duration-300 group cursor-pointer border-2 hover:border-primary/50 bg-gradient-to-br ${industry.gradient} h-full`}>
                    <industry.icon className="h-10 w-10 sm:h-12 sm:w-12 text-primary mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="font-inter text-lg sm:text-xl font-semibold text-foreground mb-2">
                      {industry.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 min-h-[2.5rem]">
                      {industry.description}
                    </p>
                    <div className="flex items-center text-primary text-sm font-medium group-hover:translate-x-2 transition-transform">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </Card>
                </LocalizedLink>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Commercial Benefits
              </h2>
              <p className="text-muted-foreground text-lg">
                Comprehensive support for your business
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-background rounded-lg shadow-soft hover:shadow-luxury transition-all"
                >
                  <benefit.icon className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-foreground font-medium text-lg">{benefit.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Products Showcase */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Premium Commercial Models
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Industry-leading smart toilets designed for high-traffic commercial environments with exceptional durability and performance
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="order-2 lg:order-1">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm text-primary font-medium">
                    <Award className="h-4 w-4" />
                    Best for Hotels & Resorts
                  </div>
                  <h3 className="font-inter text-3xl font-bold text-foreground">
                    Luna M9 Commercial Series
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    Premium smart toilet with advanced features including auto-open/close, heated seat, warm air dryer, and customizable water temperature. Perfect for luxury hospitality environments where guest experience is paramount.
                  </p>
                  <ul className="space-y-3">
                    {[
                      "High-capacity tank for frequent use",
                      "Self-cleaning nozzle technology",
                      "Energy-efficient operation",
                      "Quiet night mode for hospitality"
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button size="lg" asChild>
                    <LocalizedLink to="/contact">Request Commercial Quote</LocalizedLink>
                  </Button>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <OptimizedImage
                  src={lunaProductImg}
                  alt="Luna M9 Commercial Smart Toilet"
                  className="rounded-xl shadow-luxury w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <OptimizedImage
                  src={ariaProductImg}
                  alt="Aria Series Commercial Smart Toilet"
                  className="rounded-xl shadow-luxury w-full"
                />
              </div>
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-sm text-accent font-medium">
                  <Users className="h-4 w-4" />
                  Ideal for Corporate & Healthcare
                </div>
                <h3 className="font-inter text-3xl font-bold text-foreground">
                  Aria Commercial Series
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Designed for high-traffic commercial applications with robust construction and easy maintenance. Features antimicrobial surfaces and touchless operation for enhanced hygiene in healthcare and corporate settings.
                </p>
                <ul className="space-y-3">
                  {[
                    "Antimicrobial ceramic coating",
                    "Touchless sensors for hygiene",
                    "Easy maintenance design",
                    "ADA compliant options available"
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button size="lg" variant="outline" asChild>
                  <LocalizedLink to="/contact">Schedule Consultation</LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Advantages */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Choose Aureum for Your Business
              </h2>
              <p className="text-muted-foreground text-lg">
                Comprehensive commercial solutions backed by expertise
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: "Extended Warranties",
                  description: "5-year commercial warranty with optional service plans for peace of mind and budget predictability"
                },
                {
                  icon: Clock,
                  title: "Fast Turnaround",
                  description: "Expedited shipping and installation scheduling to minimize disruption to your business operations"
                },
                {
                  icon: Users,
                  title: "Dedicated Account Manager",
                  description: "Personal point of contact for all your needs, from initial consultation to ongoing support"
                }
              ].map((advantage, idx) => (
                <Card key={idx} className="p-8 hover:shadow-luxury transition-all">
                  <advantage.icon className="h-12 w-12 text-primary mb-4" />
                  <h3 className="font-inter text-xl font-semibold text-foreground mb-3">
                    {advantage.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {advantage.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Office & Workplace Solutions */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-sm text-accent font-medium mb-4">
                <Briefcase className="h-4 w-4" />
                Office & Corporate Solutions
              </div>
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Transform Your Workplace Facilities
              </h2>
              <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
                Modern offices demand premium amenities. Smart toilet installations enhance employee satisfaction, reduce maintenance costs, and demonstrate corporate commitment to sustainability and innovation.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="p-8 bg-gradient-to-br from-accent/5 to-background">
                <h3 className="text-2xl font-bold mb-6">Office Building Benefits</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Employee Wellness:</strong>
                      <p className="text-muted-foreground mt-1">Premium facilities boost morale and demonstrate company values</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Water Savings:</strong>
                      <p className="text-muted-foreground mt-1">30-40% reduction in water costs across your office building</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">Reduced Maintenance:</strong>
                      <p className="text-muted-foreground mt-1">Self-cleaning technology minimizes janitorial workload</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-foreground">LEED Credits:</strong>
                      <p className="text-muted-foreground mt-1">Water-efficient fixtures contribute to green building certification</p>
                    </div>
                  </li>
                </ul>
              </Card>

              <Card className="p-8 bg-gradient-to-br from-primary/5 to-background">
                <h3 className="text-2xl font-bold mb-6">Ideal Office Applications</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    <div>
                      <strong className="text-foreground">Executive Suites:</strong>
                      <p className="text-muted-foreground text-sm">Premium restrooms for C-suite and VIP areas</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    <div>
                      <strong className="text-foreground">Multi-Tenant Buildings:</strong>
                      <p className="text-muted-foreground text-sm">Attract premium tenants with luxury amenities</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    <div>
                      <strong className="text-foreground">Co-Working Spaces:</strong>
                      <p className="text-muted-foreground text-sm">Differentiate with premium facilities</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    <div>
                      <strong className="text-foreground">Corporate Campuses:</strong>
                      <p className="text-muted-foreground text-sm">Large-scale installations with volume pricing</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    <div>
                      <strong className="text-foreground">Professional Offices:</strong>
                      <p className="text-muted-foreground text-sm">Law firms, consultancies, financial services</p>
                    </div>
                  </li>
                </ul>
              </Card>
            </div>

          </div>
        </section>

        {/* Complete Solutions & Cross-Selling */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Complete Bathroom Solutions
              </h2>
              <p className="text-muted-foreground text-lg">
                Comprehensive packages for your entire facility upgrade
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="p-8 hover:shadow-luxury transition-all">
                <div className="text-4xl mb-4">🏢</div>
                <h3 className="text-xl font-semibold mb-3">Executive Suite Package</h3>
                <p className="text-muted-foreground mb-6">
                  Premium fixtures for high-profile areas including smart toilets, sensor faucets, and touchless accessories
                </p>
                <ul className="space-y-2 text-sm mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span><LocalizedLink to="/products/luxa" className="text-primary hover:underline">LUXA</LocalizedLink> or <LocalizedLink to="/products/nova" className="text-primary hover:underline">NOVA</LocalizedLink> Smart Toilet</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Premium sensor faucets</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Touchless soap dispensers</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Hand dryers & accessories</span>
                  </li>
                </ul>
                <Button className="w-full" asChild>
                  <LocalizedLink to="/contact">Request Package Quote</LocalizedLink>
                </Button>
              </Card>

              <Card className="p-8 hover:shadow-luxury transition-all border-2 border-primary/30">
                <div className="text-4xl mb-4">💼</div>
                <div className="inline-flex px-3 py-1 bg-primary/10 rounded-full text-xs font-semibold text-primary mb-4">
                  Most Popular
                </div>
                <h3 className="text-xl font-semibold mb-3">Office Standard Package</h3>
                <p className="text-muted-foreground mb-6">
                  Quality smart toilets and fixtures perfect for high-traffic office environments
                </p>
                <ul className="space-y-2 text-sm mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span><LocalizedLink to="/products/aria" className="text-primary hover:underline">ARIA</LocalizedLink> or <LocalizedLink to="/products/vena" className="text-primary hover:underline">VENA</LocalizedLink> Smart Toilet</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Standard sensor faucets</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Automatic dispensers</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Essential accessories</span>
                  </li>
                </ul>
                <Button className="w-full" asChild>
                  <LocalizedLink to="/contact">Request Package Quote</LocalizedLink>
                </Button>
              </Card>

              <Card className="p-8 hover:shadow-luxury transition-all">
                <div className="text-4xl mb-4">🏥</div>
                <h3 className="text-xl font-semibold mb-3">Healthcare Package</h3>
                <p className="text-muted-foreground mb-6">
                  Antimicrobial fixtures and touchless operation for healthcare facilities
                </p>
                <ul className="space-y-2 text-sm mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span><LocalizedLink to="/products/aria" className="text-primary hover:underline">ARIA</LocalizedLink> Smart Toilet (ADA)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Antimicrobial coatings</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Full touchless operation</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span>Compliance fixtures</span>
                  </li>
                </ul>
                <Button className="w-full" variant="outline" asChild>
                  <LocalizedLink to="/contact">Request Package Quote</LocalizedLink>
                </Button>
              </Card>
            </div>

            <Card className="p-8 bg-gradient-to-br from-accent/5 to-primary/5">
              <h3 className="text-2xl font-bold mb-4">Service Add-Ons</h3>
              <p className="text-muted-foreground mb-6">
                Enhance your purchase with comprehensive service packages for worry-free operation
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Extended Warranty Plans</h4>
                    <p className="text-sm text-muted-foreground">5 or 10-year coverage with priority support and replacement guarantees</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Staff Training</h4>
                    <p className="text-sm text-muted-foreground">On-site training for facilities teams on maintenance and troubleshooting</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Preventive Maintenance</h4>
                    <p className="text-sm text-muted-foreground">Scheduled inspections and maintenance to maximize system lifespan</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">24/7 Commercial Support</h4>
                    <p className="text-sm text-muted-foreground">Dedicated hotline for urgent issues with 4-hour response time</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Developer & Bulk Pricing */}
        <section className="py-20 px-4" id="bulk-pricing">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-primary">Best Price Guarantee · MOQ Ready</p>
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Developer &amp; Bulk Pricing — Smart Toilet Line
              </h2>
              <p className="text-muted-foreground max-w-3xl mx-auto text-lg">
                The same factory-direct model we run on bathware, applied to the core smart
                toilet range. Tell us the models, unit count and delivery schedule, and we
                quote the lowest MOQ and the most competitive per-unit price for the project.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3 mb-12">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-2 text-foreground">Volume Tiers</h3>
                <p className="text-sm text-muted-foreground">
                  Pricing steps down with unit count across the whole line — REM7, LUXA, ARIA,
                  LUNA, NOVA and the REM7 variants — quoted per project rather than as a fixed list.
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-2 text-foreground">Freight Options</h3>
                <p className="text-sm text-muted-foreground">
                  Air freight in 10–15 days on stocked SKUs for phased handovers and mock-up
                  rooms; sea freight in around 40 days, approx. 240 units per 40-ft container
                  for full rollouts.
                </p>
              </Card>
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-2 text-foreground">OEM &amp; Custom</h3>
                <p className="text-sm text-muted-foreground">
                  OEM and white-label options, custom finishes and branded packaging available
                  from defined minimum order quantities.
                </p>
              </Card>
            </div>

            <div className="border-2 border-primary/40 bg-gradient-to-br from-primary/5 to-transparent p-8 lg:p-12 rounded-xl">
              <div className="grid gap-8 lg:grid-cols-3 lg:items-center">
                <div className="lg:col-span-2">
                  <h3 className="font-inter text-2xl font-bold text-foreground sm:text-3xl">
                    One supply agreement for the whole bathroom.
                  </h3>
                  <p className="mt-4 text-muted-foreground leading-relaxed">
                    Smart toilets can be quoted alongside bathtubs, whirlpool spas and vanities
                    from the{" "}
                    <LocalizedLink to="/bath-sanitary" className="text-primary underline underline-offset-4">
                      Bath &amp; Sanitary collection
                    </LocalizedLink>{" "}
                    on a single agreement — one lead time, one shipment plan, one point of contact.
                    For the underlying per-unit prices, see the{" "}
                    <LocalizedLink to="/smart-toilet-cost" className="text-primary underline underline-offset-4">
                      smart toilet cost guide
                    </LocalizedLink>
                    .
                  </p>
                  <ul className="mt-6 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                    <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> Factory-direct pricing, no distributor markup</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> Flexible MOQ for staged developments</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> CE &amp; WaterSense certified, EU 230V</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> EU sea &amp; air freight with tracked lead times</li>
                  </ul>
                </div>
                <div className="flex flex-col gap-3">
                  <Button asChild size="lg">
                    <LocalizedLink to="/contact">Request Bulk Pricing</LocalizedLink>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="https://wa.me/34642278939?text=Hello%20Aureum%20VII%2C%20I%27d%20like%20developer%20bulk%20pricing%20for%20smart%20toilets." target="_blank" rel="noopener noreferrer">
                      WhatsApp a Specialist
                    </a>
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">Reply within 1 business day</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Simple Process */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-4">
                Simple Process
              </h2>
              <p className="text-muted-foreground text-lg">
                Getting started is easy
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold shadow-lg">
                      {item.step}
                    </div>
                    <h3 className="font-inter text-xl font-semibold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                  
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-primary/50 to-accent/50" />
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button size="lg" className="text-lg px-8 h-14" asChild>
                <LocalizedLink to="/contact">
                  Get Started Today
                </LocalizedLink>
              </Button>
            </div>
          </div>
        </section>

        {/* Specification & Procurement Resources */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground mb-3 text-center">
              Specification &amp; Procurement Resources
            </h2>
            <p className="text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
              Technical guides for architects, developers and hospitality procurement teams.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  to: "/blog/specifying-smart-toilets-multi-unit-residential",
                  title: "Specifying Smart Toilets for Multi-Unit Residential",
                  desc: "Rough-in planning, electrical provision and procurement sequencing for apartment and villa developments.",
                },
                {
                  to: "/blog/smart-toilet-roi-boutique-hotels",
                  title: "Smart Toilet ROI for Boutique Hotels",
                  desc: "How to model capex, installation, consumables and maintenance honestly for a hospitality rollout.",
                },
                {
                  to: "/blog/smart-toilet-spec-sheet-guide",
                  title: "How to Write a Smart Toilet Spec Sheet",
                  desc: "Every line item to include: dimensions, power, water pressure, clearances and rough-in detail.",
                },
              ].map((item) => (
                <LocalizedLink key={item.to} to={item.to}>
                  <Card className="p-6 h-full hover:shadow-luxury transition-all group">
                    <h3 className="font-inter text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{item.desc}</p>
                    <span className="text-sm text-primary inline-flex items-center gap-1">
                      Read guide <ArrowRight className="h-4 w-4" />
                    </span>
                  </Card>
                </LocalizedLink>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <Card className="p-12 bg-gradient-to-br from-primary/5 to-accent/5 border-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <h2 className="font-inter text-3xl md:text-4xl font-bold text-foreground">
                    Ready to Upgrade Your Facilities?
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    Our B2B specialists are ready to discuss your project requirements, provide detailed quotes, and create a customized solution for your business.
                  </p>
                  
                  <div className="space-y-4 pt-4">
                    <a 
                      href="mailto:info@aureum7trading.com"
                      className="flex items-center gap-4 p-4 bg-background rounded-lg hover:shadow-luxury transition-all group"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Email Us</div>
                        <div className="text-foreground font-semibold">info@aureum7trading.com</div>
                      </div>
                    </a>

                    <a 
                      href="tel:+34952123456"
                      className="flex items-center gap-4 p-4 bg-background rounded-lg hover:shadow-luxury transition-all group"
                    >
                      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                        <Phone className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Call Us</div>
                        <div className="text-foreground font-semibold">+34 952 123 456</div>
                      </div>
                    </a>

                    <div className="flex items-center gap-4 p-4 bg-background rounded-lg">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Clock className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Business Hours</div>
                        <div className="text-foreground font-semibold">Mon-Fri: 9:00 AM - 6:00 PM CET</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <OptimizedImage
                    src={officeImg}
                    alt="Aureum VII Trading Office"
                    className="rounded-xl shadow-luxury w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="mt-12 pt-12 border-t border-border text-center">
                <p className="text-muted-foreground mb-6">
                  Prefer to fill out a form? Visit our contact page for more options.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" asChild>
                    <LocalizedLink to="/contact">
                      <Mail className="mr-2 h-5 w-5" />
                      Contact Form
                    </LocalizedLink>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <a href="mailto:info@aureum7trading.com">
                      <Mail className="mr-2 h-5 w-5" />
                      Email Directly
                    </a>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Business;
